import React from "react";
import {
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
  Box,
  Button,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PhoneIcon from "@mui/icons-material/Phone";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import {
  CareGapStatusBadge,
  WorkflowStatusBadge,
  HistoricallyNonCompliantBadge,
  ContractualBadge,
} from "../shared/Badges";
import { CARE_GAP_STATUS_COLORS } from "../../common/constants";

const fmtDate = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return String(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function CareGapCard({ careGap, onSelect, onAction, expanded = false }) {
  if (!careGap) return null;

  const statusKey = careGap.careGapStatusKey || "open";
  const statusColors = CARE_GAP_STATUS_COLORS[statusKey] || CARE_GAP_STATUS_COLORS.open;

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: `5px solid ${statusColors.border}`,
        opacity: statusKey === "closed" ? 0.75 : 1,
        transition: "transform .2s, box-shadow .2s, border-radius .2s",
        borderRadius: expanded ? "8px 8px 0 0" : 2,
        borderBottom: expanded ? "none" : undefined,
        boxShadow: expanded ? 4 : 0,
        "&:hover": expanded
          ? { boxShadow: 4 }
          : { boxShadow: 3, transform: "translateY(-1px)" },
      }}
    >
      <CardActionArea onClick={() => onSelect?.(careGap)}>
        <CardContent>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ mb: 1 }}>
            <CareGapStatusBadge statusKey={statusKey} statusName={careGap.careGapStatusName} />
            <WorkflowStatusBadge
              workflowKey={careGap.workflowStatusKey}
              workflowName={careGap.workflowStatusName}
            />
            {careGap.contractual ? <ContractualBadge qmCode={careGap.qmCode} /> : null}
            {careGap.historicallyNonCompliant ? <HistoricallyNonCompliantBadge /> : null}
          </Stack>

          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            {careGap.hedisDisplayName || careGap.careGapName}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <strong>{careGap.patientFullName}</strong>
            {careGap.cipId ? `  ·  CIP ${careGap.cipId}` : ""}
            {careGap.patientDob ? `  ·  DOB ${fmtDate(careGap.patientDob)}` : ""}
            {careGap.patientAge ? `  ·  ${careGap.patientAge}a` : ""}
            {careGap.patientGender ? `  ·  ${careGap.patientGender}` : ""}
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            PCP: {careGap.pcpFullName || "Unassigned"}
            {careGap.officeLocation ? `  ·  ${careGap.officeLocation}` : ""}
          </Typography>

          {(careGap.patientPhone || careGap.patientMobile) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {careGap.patientPhone ? `📞 ${careGap.patientPhone}` : ""}
              {careGap.patientPhone && careGap.patientMobile ? "   " : ""}
              {careGap.patientMobile ? `📱 ${careGap.patientMobile}` : ""}
            </Typography>
          )}

          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Workflow updated: {fmtDate(careGap.workflowUpdated)} · Last seen on Hability: {fmtDate(careGap.lastSeen)}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>

      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 2, pb: 2, flexWrap: "wrap" }}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          size="small"
          startIcon={<CalendarMonthIcon />}
          onClick={() => onAction?.("schedule", careGap)}
          variant="outlined"
        >
          Schedule
        </Button>
        <Button
          size="small"
          startIcon={<PhoneIcon />}
          onClick={() => onAction?.("outreach", careGap)}
          variant="outlined"
        >
          Outreach
        </Button>
        <Button
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={() => onAction?.("submit", careGap)}
          variant="outlined"
          color="success"
        >
          Submit
        </Button>
        <Button
          size="small"
          startIcon={<MoreVertIcon />}
          onClick={() => onAction?.("workflow", careGap)}
          variant="outlined"
          color="inherit"
        >
          Care Gap Actions
        </Button>
      </Stack>
    </Card>
  );
}
